import { Spinner } from "@ui/components/spinner";
import { cn } from "@ui/lib";
import {
	CheckIcon,
	MinusIcon,
	PackageIcon,
	PlusIcon,
	Trash2Icon,
} from "lucide-react";
import { memo, useState } from "react";
import { fill } from "./copy";
import { useCopy } from "./copy-context";
import { type BasketPick, formatMoney, type ProductCard } from "./types";

/** Sets a basket quantity; resolves false when the basket refused it. */
export type SetQuantity = (
	productId: string,
	quantity: number,
) => Promise<boolean>;

/** `pending` while `run`'s task is in flight: one tap, one request. */
export function usePending() {
	const [pending, setPending] = useState(false);
	const run = async (task: () => Promise<unknown>) => {
		setPending(true);
		try {
			await task();
		} finally {
			setPending(false);
		}
	};
	return [pending, run] as const;
}

/**
 * A product the assistant found. Round add button on the photo like the
 * retailer's own tiles; once in the basket it becomes a stepper across the
 * photo's foot, so the card never changes height and the shopper never has
 * to open the basket to fix a count.
 */
export function ProductCardView({
	product,
	quantity,
	onQuantity,
	onOpen,
	addable = true,
	arabic = false,
	index = 0,
}: {
	/** The + on the photo (`chat.addFromCards`). A product already in the basket keeps its stepper. */
	addable?: boolean;
	product: ProductCard;
	/** How many are already in the basket. */
	quantity: number;
	onQuantity: SetQuantity;
	/** Tap the photo or name for the product sheet. */
	onOpen: (product: ProductCard) => void;
	/** The shopper writes in Arabic script: show the Arabic name when there is one. */
	arabic?: boolean;
	/** Position in the list, for the staggered entrance. */
	index?: number;
}) {
	const title = (arabic && product.titleAr) || product.title;

	return (
		<article
			style={{ animationDelay: `${Math.min(index, 8) * 45}ms` }}
			className={cn(
				"flex h-full min-w-0 animate-shop-rise flex-col overflow-hidden rounded-[calc(var(--radius)*2.5)] bg-(--shop-card) ring-1 ring-(--shop-brand-line)",
				!product.inStock && "opacity-70",
			)}
		>
			<div className="relative aspect-square bg-white">
				<button
					type="button"
					onClick={() => onOpen(product)}
					aria-label={`Details for ${product.title}`}
					className="absolute inset-0 z-0 rounded-t-[calc(var(--radius)*2.5)] outline-none focus-visible:ring-3 focus-visible:ring-(--shop-brand)/50 focus-visible:ring-inset"
				/>
				{product.imageUrl ? (
					<img
						src={product.imageUrl}
						alt=""
						loading="lazy"
						decoding="async"
						className={cn(
							"pointer-events-none size-full object-contain p-3",
							!product.inStock && "grayscale",
						)}
					/>
				) : (
					<NoPhoto />
				)}
				<CardBadges product={product} />
				<CardBasketControl
					product={product}
					quantity={quantity}
					onQuantity={onQuantity}
					addable={addable}
				/>
			</div>
			<div className="flex flex-1 flex-col gap-0.5 px-3 pt-2.5 pb-3">
				<h3
					dir="auto"
					className="font-semibold text-(--shop-ink) text-[13.5px] leading-snug"
				>
					<button
						type="button"
						onClick={() => onOpen(product)}
						className="line-clamp-2 text-start outline-none hover:underline focus-visible:underline"
					>
						{title}
					</button>
				</h3>
				{product.size && !mentions(title, product.size) ? (
					<p className="truncate text-(--shop-muted) text-xs">
						{product.size}
					</p>
				) : null}
				<Price product={product} className="mt-auto pt-1.5" />
			</div>
		</article>
	);
}

/** On the photo: out of stock or the offer on the start side, 18+ on the end side. */
function CardBadges({ product }: { product: ProductCard }) {
	const copy = useCopy();
	const discount = saleDiscount(product);
	return (
		<>
			{!product.inStock ? (
				<span className="pointer-events-none absolute start-2 top-2 rounded-full bg-(--shop-card)/95 px-2 py-0.5 font-medium text-(--shop-muted) text-[11px] ring-1 ring-(--shop-ink)/5">
					{copy.outOfStock}
				</span>
			) : discount !== null ? (
				<span className="pointer-events-none absolute start-2 top-2 rounded-full bg-(--shop-accent) px-2 py-0.5 font-bold text-(--shop-accent-ink) text-[11px] tabular">
					{discount > 0
						? fill(copy.percentOff, { percent: discount })
						: copy.offer}
				</span>
			) : null}
			{product.ageRestricted ? (
				<span
					title="ID checked at delivery"
					className="pointer-events-none absolute end-2 top-2 rounded-full bg-(--shop-ink) px-1.5 py-0.5 font-bold text-(--shop-card) text-[11px]"
				>
					18+
				</span>
			) : null}
		</>
	);
}

/** The percent off of an in-stock product on offer; null when it is not on offer. */
function saleDiscount(product: ProductCard): number | null {
	if (!product.salePrice || !product.inStock) {
		return null;
	}
	return Math.round(
		(1 - Number(product.salePrice) / Number(product.price)) * 100,
	);
}

/** The + on the photo, or the stepper across its foot once the product is in the basket. */
function CardBasketControl({
	product,
	quantity,
	onQuantity,
	addable,
}: {
	product: ProductCard;
	quantity: number;
	onQuantity: SetQuantity;
	addable: boolean;
}) {
	const [adding, run] = usePending();
	if (quantity > 0) {
		return (
			<QuantityStepper
				title={product.title}
				quantity={quantity}
				onChange={(next) => onQuantity(product.id, next)}
				className="absolute inset-x-2 bottom-2 z-10 shadow-[0_4px_12px_-2px_rgb(0_0_40/0.3)]"
			/>
		);
	}
	if (!addable || !product.inStock) {
		return null;
	}
	return (
		<button
			type="button"
			disabled={adding}
			onClick={() => run(() => onQuantity(product.id, 1))}
			aria-label={`Add ${product.title} to basket`}
			className="press absolute end-2 bottom-2 z-10 flex size-10 items-center justify-center rounded-(--shop-btn) bg-(--shop-brand) text-(--shop-on-brand) shadow-[0_4px_12px_-2px_rgb(0_0_40/0.3)] outline-none focus-visible:ring-3 focus-visible:ring-(--shop-brand)/40 focus-visible:ring-offset-2 disabled:opacity-70"
		>
			{adding ? (
				<Spinner className="size-4" />
			) : (
				<PlusIcon className="size-5" strokeWidth={2.6} />
			)}
		</button>
	);
}

/**
 * Every product one search returned. One or two sit side by side; more
 * scroll sideways, so a page of shampoos is one row under the answer instead
 * of three screens of grid.
 */
export const ProductResults = memo(function ProductResults({
	products,
	quantities,
	onQuantity,
	onOpen,
	arabic,
	addable = true,
}: {
	addable?: boolean;
	products: ProductCard[];
	quantities: Map<string, number>;
	onQuantity: SetQuantity;
	onOpen: (product: ProductCard) => void;
	arabic: boolean;
}) {
	const cards = products.map((product, index) => (
		<li
			key={product.id}
			className={cn(
				products.length > 2 &&
					"w-[42%] min-w-36 max-w-48 shrink-0 snap-start",
			)}
		>
			<ProductCardView
				index={index}
				product={product}
				arabic={arabic}
				quantity={quantities.get(product.id) ?? 0}
				onQuantity={onQuantity}
				onOpen={onOpen}
				addable={addable}
			/>
		</li>
	));
	return products.length > 2 ? (
		<ul
			aria-label={`${products.length} products`}
			dir={arabic ? "rtl" : "ltr"}
			className="no-scrollbar -mx-4 flex snap-x snap-mandatory scroll-px-4 gap-2.5 overflow-x-auto px-4 py-0.5"
		>
			{cards}
		</ul>
	) : (
		<ul
			dir={arabic ? "rtl" : "ltr"}
			className="grid grid-cols-2 gap-2.5 @xl:grid-cols-3"
		>
			{cards}
		</ul>
	);
});

/** A product without a photo: one placeholder everywhere. */
export function NoPhoto({ className }: { className?: string }) {
	return (
		<span
			aria-hidden
			className={cn(
				"flex size-full items-center justify-center bg-(--shop-brand-soft) text-(--shop-muted)",
				className,
			)}
		>
			<PackageIcon
				className="size-1/3 max-h-8 max-w-8"
				strokeWidth={1.5}
			/>
		</span>
	);
}

/** "Fresh Milk 1 L" already says "1 l": don't print the size twice. */
function mentions(title: string, size: string): boolean {
	const squash = (text: string) => text.toLowerCase().replace(/\s+/g, "");
	return squash(title).includes(squash(size));
}

/** One tap for every in-stock product; turns into "add the other N", then a tick. */
export function AddAllButton({
	picks,
	quantities,
	onAddAll,
}: {
	picks: BasketPick[];
	quantities: Map<string, number>;
	onAddAll: (picks: BasketPick[]) => Promise<boolean>;
}) {
	const copy = useCopy();
	const [adding, run] = usePending();
	const addable = picks.filter((p) => p.product.inStock);
	// A pick is in when the basket holds as many as the line needs.
	const missing = addable.filter(
		(p) => (quantities.get(p.product.id) ?? 0) < p.quantity,
	);
	const total = missing.reduce(
		(sum, { product, quantity }) =>
			sum +
			Number(product.salePrice ?? product.price) *
				(quantity - (quantities.get(product.id) ?? 0)),
		0,
	);
	const currency = picks[0]?.product.currency ?? "USD";
	const allIn = addable.length > 0 && missing.length === 0;

	return (
		<button
			type="button"
			disabled={adding || allIn}
			onClick={() => run(() => onAddAll(missing))}
			className={cn(
				"press flex h-11 items-center justify-center gap-2 rounded-(--shop-btn) px-4 font-semibold text-sm outline-none transition-colors focus-visible:ring-3 focus-visible:ring-(--shop-brand)/35",
				allIn
					? "bg-(--shop-accent-soft) text-(--shop-accent-ink)"
					: "bg-(--shop-brand-soft) text-(--shop-brand-text) hover:bg-(--shop-brand-line)",
			)}
		>
			{adding ? (
				<Spinner className="size-4" />
			) : allIn ? (
				<CheckIcon className="size-4" strokeWidth={2.6} />
			) : (
				<PlusIcon className="size-4" strokeWidth={2.6} />
			)}
			{allIn
				? copy.allInBasket(addable.length)
				: `${
						missing.length === addable.length
							? copy.addAll(addable.length)
							: copy.addOthers(missing.length)
					} · ${formatMoney(total.toFixed(2), currency)}`}
		</button>
	);
}

const PRICE = {
	sm: { now: "font-bold text-[15px]", was: "text-xs" },
	lg: { now: "font-extrabold text-2xl", was: "text-sm" },
} as const;

/** Price as the shopper pays it, with the old price struck through on offer. */
export function Price({
	product,
	size = "sm",
	className,
	children,
}: {
	product: ProductCard;
	size?: keyof typeof PRICE;
	className?: string;
	/** Anything that belongs on the price line, such as the pack size. */
	children?: React.ReactNode;
}) {
	const onSale = Boolean(product.salePrice) && product.inStock;
	return (
		<div
			className={cn(
				"flex flex-wrap items-baseline gap-x-1.5 gap-y-1 tabular",
				className,
			)}
		>
			<span
				className={cn(
					PRICE[size].now,
					onSale ? "text-(--shop-deal)" : "text-(--shop-ink)",
				)}
			>
				{formatMoney(
					product.salePrice ?? product.price,
					product.currency,
				)}
			</span>
			{onSale ? (
				<span
					className={cn(
						"text-(--shop-muted) line-through",
						PRICE[size].was,
					)}
				>
					{formatMoney(product.price, product.currency)}
				</span>
			) : null}
			{children}
		</div>
	);
}

const STEPPER = {
	/** On a card, a recipe line, the call. */
	sm: {
		box: "p-0.5",
		button: "size-9",
		icon: "size-4",
		bin: "size-3.5",
		count: "font-bold text-sm",
	},
	/** The product sheet's main action. */
	lg: {
		box: "h-13 p-1",
		button: "size-11",
		icon: "size-5",
		bin: "size-4.5",
		count: "font-extrabold text-lg",
	},
} as const;

/**
 * The basket count of one product: minus (a bin at 1), the count, plus.
 * Holds its own pending state, so a tap can't fire twice.
 */
export function QuantityStepper({
	title,
	quantity,
	onChange,
	size = "sm",
	tone = "brand",
	canAdd = true,
	label = String,
	className,
}: {
	title: string;
	quantity: number;
	onChange: (quantity: number) => Promise<unknown>;
	size?: keyof typeof STEPPER;
	/** "soft" on a light surface (the basket), "brand" everywhere else. */
	tone?: "brand" | "soft";
	/** False for a line gone out of stock: it can only go down. */
	canAdd?: boolean;
	label?: (quantity: number) => string;
	className?: string;
}) {
	const [pending, run] = usePending();
	const look = STEPPER[size];
	const button = cn(
		"relative flex items-center justify-center rounded-(--shop-btn) outline-none after:absolute after:-inset-0.5 disabled:opacity-50",
		look.button,
		tone === "brand"
			? "hover:bg-(--shop-on-brand)/15 focus-visible:ring-2 focus-visible:ring-(--shop-on-brand)/60"
			: "hover:bg-(--shop-brand-line) focus-visible:ring-2 focus-visible:ring-(--shop-brand)/40",
	);
	return (
		<div
			className={cn(
				"flex items-center justify-between rounded-(--shop-btn) transition-opacity",
				look.box,
				tone === "brand"
					? "bg-(--shop-brand) text-(--shop-on-brand)"
					: "gap-1 bg-(--shop-brand-soft) text-(--shop-brand-text)",
				pending && "opacity-70",
				className,
			)}
		>
			<button
				type="button"
				disabled={pending}
				onClick={() => run(() => onChange(quantity - 1))}
				aria-label={
					quantity === 1 ? `Remove ${title}` : `One less ${title}`
				}
				className={button}
			>
				{quantity === 1 ? (
					<Trash2Icon className={look.bin} />
				) : (
					<MinusIcon className={look.icon} />
				)}
			</button>
			<span
				className={cn("min-w-6 text-center tabular", look.count)}
				aria-live="polite"
			>
				{label(quantity)}
			</span>
			<button
				type="button"
				disabled={pending || !canAdd}
				onClick={() => run(() => onChange(quantity + 1))}
				aria-label={`One more ${title}`}
				className={button}
			>
				<PlusIcon className={look.icon} />
			</button>
		</div>
	);
}
